import { injectStyles } from './styles.js';

// Two-step modal, mirroring hubculture.com's own login:
//   step 1 — email only, submit reads "Get started"; the email is checked
//            against /user/checkEmail before moving on
//   step 2 — email shown read-only with an "Edit email" link, plus password,
//            show/hide toggle, remember me and a forgot-password link
export function createModal({ theme, onCheckEmail, onSubmit, onClose }) {
  injectStyles();

  const overlay = document.createElement('div');
  overlay.className = 'hublogin-overlay';
  overlay.setAttribute('role', 'presentation');

  overlay.innerHTML = `
    <div class="hublogin-card" role="dialog" aria-modal="true" aria-labelledby="hublogin-title" data-step="1">
      <button type="button" class="hublogin-close" aria-label="Close">&times;</button>
      ${theme.logo ? `<img class="hublogin-logo" src="${theme.logo}" alt="${theme.title || 'Hub Culture'}" />` : ''}
      <h2 class="hublogin-title" id="hublogin-title">${theme.title || 'Sign in to Hub Culture'}</h2>
      ${theme.subtitle ? `<p class="hublogin-subtitle">${theme.subtitle}</p>` : ''}

      <form novalidate>
        <div class="hublogin-field hublogin-only-1">
          <label class="hublogin-label" for="hublogin-email">Email</label>
          <input class="hublogin-input" id="hublogin-email" type="email" autocomplete="username" required />
        </div>

        <div class="hublogin-identity hublogin-only-2">
          <span class="hublogin-identity-email"></span>
          <button type="button" class="hublogin-edit-email">Edit email</button>
        </div>

        <div class="hublogin-field hublogin-only-2">
          <div class="hublogin-label-row">
            <label class="hublogin-label" for="hublogin-password">Password</label>
            <button type="button" class="hublogin-pass-toggle">Show</button>
          </div>
          <input class="hublogin-input" id="hublogin-password" type="password" autocomplete="current-password" />
        </div>

        <label class="hublogin-remember hublogin-only-2">
          <input type="checkbox" id="hublogin-remember" /> <span>Remember me</span>
        </label>

        <p class="hublogin-error"></p>
        <button type="submit" class="hublogin-submit" disabled>Get started</button>
      </form>

      <div class="hublogin-footer">
        <span class="hublogin-only-1">
          ${theme.registerUrl ? `Don't have a HubID? <a href="${theme.registerUrl}" target="_blank" rel="noopener">Register</a>` : ''}
        </span>
        <span class="hublogin-only-2">
          ${theme.forgotUrl ? `<a href="${theme.forgotUrl}" target="_blank" rel="noopener">I forgot my password</a>` : ''}
        </span>
      </div>
    </div>
  `;

  overlay.style.setProperty('--hublogin-primary', theme.primaryColor || '#16407C');
  if (theme.primaryColorHover) overlay.style.setProperty('--hublogin-primary-hover', theme.primaryColorHover);
  if (theme.accentColor) overlay.style.setProperty('--hublogin-accent', theme.accentColor);

  document.body.appendChild(overlay);

  const card = overlay.querySelector('.hublogin-card');
  const form = overlay.querySelector('form');
  const emailInput = overlay.querySelector('#hublogin-email');
  const passwordInput = overlay.querySelector('#hublogin-password');
  const rememberInput = overlay.querySelector('#hublogin-remember');
  const identityEmail = overlay.querySelector('.hublogin-identity-email');
  const editEmailBtn = overlay.querySelector('.hublogin-edit-email');
  const passToggle = overlay.querySelector('.hublogin-pass-toggle');
  const errorEl = overlay.querySelector('.hublogin-error');
  const submitBtn = overlay.querySelector('.hublogin-submit');
  const closeBtn = overlay.querySelector('.hublogin-close');

  let step = 1;
  const labelFor = (s) => (s === 1 ? 'Get started' : 'Log in');

  // The submit button follows whichever field the current step needs, matching
  // hubculture.com (disabled until the step's field has a value).
  function syncSubmitEnabled() {
    const value = step === 1 ? emailInput.value.trim() : passwordInput.value;
    submitBtn.disabled = !value;
  }

  function setStep(next) {
    step = next;
    card.setAttribute('data-step', String(next));
    submitBtn.textContent = labelFor(next);
    setError(null);
    if (next === 2) {
      identityEmail.textContent = emailInput.value.trim();
      passwordInput.type = 'password';
      passToggle.textContent = 'Show';
      setTimeout(() => passwordInput.focus(), 0);
    } else {
      passwordInput.value = '';
      rememberInput.checked = false;
      setTimeout(() => emailInput.focus(), 0);
    }
    syncSubmitEnabled();
  }

  function onKeydown(event) {
    if (event.key === 'Escape') close();
  }

  function open() {
    overlay.classList.add('is-open');
    document.addEventListener('keydown', onKeydown);
    setTimeout(() => (step === 1 ? emailInput : passwordInput).focus(), 0);
  }

  function close() {
    overlay.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);
    if (onClose) onClose();
  }

  function reset() {
    form.reset();
    setStep(1);
    setLoading(false);
  }

  function setError(message, html) {
    if (html) {
      errorEl.innerHTML = message || '';
    } else {
      errorEl.textContent = message || '';
    }
  }

  function setLoading(isLoading) {
    submitBtn.disabled = isLoading;
    submitBtn.textContent = isLoading
      ? step === 1
        ? 'Checking…'
        : 'Signing in…'
      : labelFor(step);
    if (!isLoading) syncSubmitEnabled();
  }

  emailInput.addEventListener('input', syncSubmitEnabled);
  passwordInput.addEventListener('input', syncSubmitEnabled);

  passToggle.addEventListener('click', () => {
    const showing = passwordInput.type === 'text';
    passwordInput.type = showing ? 'password' : 'text';
    passToggle.textContent = showing ? 'Show' : 'Hide';
  });

  editEmailBtn.addEventListener('click', () => setStep(1));

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (step === 1) {
      onCheckEmail({ email: emailInput.value.trim() });
    } else {
      onSubmit({
        email: emailInput.value.trim(),
        password: passwordInput.value,
        remember: rememberInput.checked,
      });
    }
  });

  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });

  function destroy() {
    document.removeEventListener('keydown', onKeydown);
    overlay.remove();
  }

  return { open, close, reset, setError, setLoading, setStep, destroy, el: overlay };
}
